import { useLocation, useNavigate } from "react-router-dom"
import { useEffect, useMemo, useRef, useState } from "react"
import { ArrowRight, Trash2, Users } from "lucide-react"
import { DeleteSmithersClientDialog } from "@/components/smithers/delete-smithers-client-dialog"
import { DashboardHeader } from "@/components/shared/dashboard-header"
import { DevicesTableSkeleton } from "@/components/shared/loading-skeletons"
import { ApiErrorState } from "@/components/shared/empty-states"
import { SortableHeader, type SortDirection } from "@/components/shared/sortable-header"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { useDeleteSmithersClient, useSmithersClients } from "@/hooks/use-smithers-clients"
import { useToast } from "@/hooks/use-toast"
import type { SmithersClientConfig } from "@/types/smithers-client"
import { getSmithersClientListHistoryState } from "@/lib/smithers-client-navigation"

export function SmithersClientsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const restored = getSmithersClientListHistoryState(location.state)
  const { data: clients = [], error, isLoading, mutate } = useSmithersClients()
  const [sortColumn, setSortColumn] = useState<"clientId" | null>(() => restored?.sortColumn ?? null)
  const [sortDirection, setSortDirection] = useState<SortDirection>(() => restored?.sortDirection ?? "asc")
  useEffect(() => {
    navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, {
      replace: true, state: { smithersClientList: { sortColumn, sortDirection } },
    })
  }, [navigate, location.pathname, location.search, location.hash, sortColumn, sortDirection])
  const listOrigin = { smithersClientList: { sortColumn, sortDirection } }
  const handleSort = (column: "clientId") => {
    if (sortColumn === column) {
      setSortDirection((current) => current === "asc" ? "desc" : "asc")
    } else {
      setSortColumn(column)
      setSortDirection("asc")
    }
  }
  const sortedClients = useMemo(() => {
    if (!sortColumn) return clients
    return [...clients].sort((a, b) => {
      const comparison = a.clientId.localeCompare(b.clientId)
      return sortDirection === "asc" ? comparison : -comparison
    })
  }, [clients, sortColumn, sortDirection])
  const addClient = () => navigate("/smithers/clients/new", { state: listOrigin })
  const deleteClient = useDeleteSmithersClient()
  const { toast } = useToast()
  const [pendingDelete, setPendingDelete] = useState<SmithersClientConfig | null>(null)
  const [deleteError, setDeleteError] = useState<string>()
  const deleting = useRef(false)
  const confirmDelete = async () => {
    if (!pendingDelete || deleting.current) return
    deleting.current = true
    setDeleteError(undefined)
    try {
      await deleteClient.trigger(pendingDelete.id)
      await mutate((current = []) => current.filter((client) => client.id !== pendingDelete.id), { revalidate: false })
      toast({ variant: "success", title: "Client deleted", description: `Client ${pendingDelete.clientId} has been deleted.` })
      setPendingDelete(null)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to delete client. Please try again."
      setDeleteError(message)
      toast({ variant: "error", title: "Failed to delete client", description: message })
    } finally {
      deleting.current = false
    }
  }

  return (
    <>
      <DashboardHeader onAddClient={addClient} />
      <main className="container mx-auto flex flex-col gap-6 px-4 py-6">
        <div>
          <h1 className="mb-2 text-2xl font-semibold text-foreground">Smithers Clients</h1>
          <p className="text-sm text-muted-foreground">Manage and view all Smithers clients in the system</p>
        </div>
        {isLoading ? <DevicesTableSkeleton /> : error ? (
          <ApiErrorState onRetry={() => void mutate()} resourceName="clients" />
        ) : clients.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <Users className="mb-4 size-12 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold text-foreground">No clients found</h3>
              <p className="mb-6 max-w-sm text-sm text-muted-foreground">There are no Smithers clients configured in the system.</p>
              <Button onClick={addClient}>Add Client</Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <div className="table-scroll-x overflow-x-auto">
                <Table className="min-w-max w-full" aria-label="Smithers clients">
                  <TableHeader>
                    <TableRow className="border-border hover:bg-card">
                      <SortableHeader label="Client ID" sortKey="clientId" currentSortColumn={sortColumn} currentSortDirection={sortDirection} onSort={handleSort} />
                      <TableHead className="text-muted-foreground font-medium w-32">Actions</TableHead>
                      <TableHead className="text-muted-foreground font-medium w-24 sticky right-0 bg-card z-10">Delete</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedClients.map((client, index) => (
                      <TableRow key={client.id} className={`border-border transition-colors group ${index % 2 === 1 ? "bg-muted/30 hover:bg-muted/50" : "hover:bg-muted/50"}`}>
                        <TableCell><div className="font-medium text-foreground">{client.clientId}</div></TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            className="bg-card border-input text-foreground hover:bg-secondary hover:text-foreground"
                            onClick={() => navigate(`/smithers/devices?${new URLSearchParams({ clientId: client.clientId })}`, { state: listOrigin })}
                          >
                            View Devices <ArrowRight className="h-4 w-4 ml-2" />
                          </Button>
                        </TableCell>
                        <TableCell className="sticky right-0 bg-card z-10 min-w-24">
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" aria-label={`Delete client ${client.clientId}`} disabled={deleteClient.isMutating} onClick={() => { setDeleteError(undefined); setPendingDelete(client) }} className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 h-8 w-8"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
      <DeleteSmithersClientDialog open={pendingDelete !== null} onOpenChange={(open) => { if (!open) { setPendingDelete(null); setDeleteError(undefined) } }} clientId={pendingDelete?.clientId ?? ""} onConfirm={() => void confirmDelete()} isLoading={deleteClient.isMutating} error={deleteError} />
    </>
  )
}
